import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardList, Search, User, Clock } from 'lucide-react';

const ACTION_COLORS = {
  checklist_update: 'bg-blue-100 text-blue-700',
  photo_upload: 'bg-violet-100 text-violet-700',
  issue_raised: 'bg-red-100 text-red-700',
  issue_resolved: 'bg-emerald-100 text-emerald-700',
  signoff_requested: 'bg-amber-100 text-amber-700',
  signoff_approved: 'bg-emerald-100 text-emerald-700',
  signoff_rejected: 'bg-red-100 text-red-700',
  trade_signoff: 'bg-teal-100 text-teal-700',
  status_override: 'bg-slate-100 text-slate-700',
  template_update: 'bg-indigo-100 text-indigo-700',
  backup_created: 'bg-slate-100 text-slate-700',
  backup_restored: 'bg-amber-100 text-amber-700',
};

function AuditEntry({ entry }) {
  const colorClass = ACTION_COLORS[entry.action] || 'bg-slate-100 text-slate-700';
  return (
    <div className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl" data-testid={`audit-entry-${entry.id}`}>
      <div className="flex-shrink-0 mt-0.5">
        <Clock className="h-4 w-4 text-slate-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`text-[10px] ${colorClass}`}>{entry.action.replace(/_/g, ' ')}</Badge>
          {entry.unit_name && <Badge variant="outline" className="text-[10px]">{entry.unit_name}</Badge>}
          {entry.package && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{entry.package}</Badge>}
          {entry.stage_type && entry.stage_number > 0 && (
            <Badge variant="outline" className="text-[10px]">{entry.stage_type} S{entry.stage_number}</Badge>
          )}
        </div>
        <p className="text-sm text-slate-800 mt-1">{entry.details}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-[10px] text-slate-500 flex items-center gap-1">
            <User className="h-2.5 w-2.5" />{entry.user_name}
            {entry.user_company ? ` (${entry.user_company})` : entry.user_role ? ` (${entry.user_role})` : ''}
          </span>
          <span className="text-[10px] text-slate-400">{new Date(entry.created_at).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

export default function AuditPage() {
  const { unitId } = useParams();
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(unitId || 'all');
  const [stageFilter, setStageFilter] = useState('all');
  const [packageFilter, setPackageFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get('/units').then(r => setUnits(r.data)).catch(() => {});
    api.get('/packages').then(r => setPackages(r.data)).catch(() => {});
  }, []);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (selectedUnit !== 'all') params.unit_id = selectedUnit;
      if (stageFilter !== 'all') params.stage_type = stageFilter;
      if (packageFilter !== 'all') params.package = packageFilter;
      const resp = await api.get('/audit/all', { params });
      setEntries(resp.data);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedUnit, stageFilter, packageFilter]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const filteredEntries = useMemo(() => {
    if (!searchTerm) return entries;
    const term = searchTerm.toLowerCase();
    return entries.filter(e =>
      (e.details || '').toLowerCase().includes(term) ||
      (e.user_name || '').toLowerCase().includes(term) ||
      (e.action || '').toLowerCase().includes(term) ||
      (e.trade || '').toLowerCase().includes(term)
    );
  }, [entries, searchTerm]);

  return (
    <Layout>
      <div className="space-y-4" data-testid="audit-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            <ClipboardList className="inline h-5 w-5 mr-2 text-blue-600" />Audit Trail
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track all actions across the project</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={selectedUnit} onValueChange={setSelectedUnit}>
            <SelectTrigger className="w-full sm:w-48" data-testid="audit-unit-filter"><SelectValue placeholder="All Units" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Units</SelectItem>
              {units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-full sm:w-36" data-testid="audit-stage-filter"><SelectValue placeholder="All Stages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              <SelectItem value="progress">Progress</SelectItem>
              <SelectItem value="qa">QA</SelectItem>
            </SelectContent>
          </Select>
          <Select value={packageFilter} onValueChange={setPackageFilter}>
            <SelectTrigger className="w-full sm:w-56" data-testid="audit-package-filter"><SelectValue placeholder="All Packages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Packages</SelectItem>
              {packages.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search actions, users, trades..." className="pl-9" data-testid="audit-search-input" />
          </div>
        </div>

        <p className="text-xs text-slate-500">{filteredEntries.length} entries</p>

        {(() => {
          if (loading) return <div className="text-center py-12 text-slate-400">Loading audit trail...</div>;
          if (filteredEntries.length === 0) return <div className="text-center py-12 text-slate-400">No audit entries found</div>;
          return (
            <div className="space-y-2">
              {filteredEntries.map(entry => <AuditEntry key={entry.id} entry={entry} />)}
            </div>
          );
        })()}
      </div>
    </Layout>
  );
}
